import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loadPowerBIReportsDataRequest,
  setSelectedReport,
  loadPowerBIEmbedUrlRequest,
  loadPowerBIAddFavoriteRequest,
  loadPowerBIRemoveFavoriteRequest,
  setProfileView
} from "../redux/Actions";
import { loadCompanyDataRequest, loadCompanyUserAddDataRequest, loadCompanyUserGetDataRequest } from "../../company/redux/Actions";
import { usePortal } from "../../../contexts/PortalContext";
import useDeviceDetect from "../../../hooks/useDeviceDetect";
import { Col } from 'react-bootstrap';
import FormikSelectField from '../../../shared/components/FormikSelectField';
import "./ReportsMenu.scss";
import "./ReportsMenuTooltip.css";
import { identity } from "@fullcalendar/core/internal";

const ReportsMenu = () => {
  const dispatch = useDispatch();
  const { isReportMenuCollapsed, toggleReportMenu, handlePageChange, currentActivePage } = usePortal();
  const { isMobile } = useDeviceDetect();
  const [searchQuery, setSearchQuery] = useState('');
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false);
  const [expandedReports, setExpandedReports] = useState({}); // Track which reports are expanded

  let iconPath = "/OPERIS_LOGO.png";
  const logoPath = "/logo.png";

  // Get reports data from Redux store
  const { loading, reports, selectedReport, error, favorites, favoriteToggling } = useSelector(
    (state) => state.powerBIReports
  );

  // Get company data from Redux store
  const { companies } = useSelector((store) => store.company);
  const { userCompany } = useSelector((store) => store.company) || {};

  // Get employee info from localStorage
  const empInfo = JSON.parse(localStorage.getItem('employee') || '{}');
  const biEmpInfo = JSON.parse(localStorage.getItem('powerbi_employee') || '{}');

  // Company selection state management (same as Header.js)
  const getStoredCompanyIds = () => {
    try {
      const raw = localStorage.getItem('emp_company');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const [emp_company, setEmpCompany] = useState(getStoredCompanyIds());
  const [pendingCompanyIds, setPendingCompanyIds] = useState(getStoredCompanyIds());

  // Fetch reports and company data from API when component mounts
  useEffect(() => {
    dispatch(loadPowerBIReportsDataRequest());
    dispatch(loadCompanyDataRequest());
    dispatch(loadCompanyUserGetDataRequest());
  }, [dispatch]);

  // Update emp_company when userCompany changes
  useEffect(() => {
    const companyIds = userCompany?.data?.company_id;
    if (Array.isArray(companyIds)) {
      localStorage.setItem("emp_company", JSON.stringify(companyIds));
      setEmpCompany(companyIds);
    }
  }, [userCompany]);

  const selectedCompanies = companies?.filter(c => emp_company.includes(c.id)) || [];

  // Show comma-separated company names
  const companySelectedLabel = selectedCompanies.length === 0
    ? "No Company Selected"
    : selectedCompanies.map(c => c.company_name).join(", ");

  const openCompanyMenu = () => {
    setPendingCompanyIds(emp_company);
    setCompanyMenuOpen(true);
  };

  const handleApplyCompanies = () => {
    localStorage.setItem("emp_company", JSON.stringify(pendingCompanyIds));
    setEmpCompany(pendingCompanyIds);
    dispatch(loadCompanyUserAddDataRequest({ "company_id": pendingCompanyIds }));
    setCompanyMenuOpen(false);
  };

  const handleCancelCompanies = () => {
    setPendingCompanyIds(emp_company);
    setCompanyMenuOpen(false);
  };

  const handleHomeClick = () => {
    dispatch(setSelectedReport(null));

    // Auto-close menu on mobile when going to home
    if (isMobile && !isReportMenuCollapsed) {
      toggleReportMenu();
    }

    // Scroll to top on mobile when home is clicked
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const mainContainer = document.querySelector('.main');
        if (mainContainer) {
          mainContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // Toggle report expansion (show/hide pages)
  const toggleReportExpansion = (event, reportId) => {
    event.stopPropagation();
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  // Handle page click - load report and navigate to specific page
  const handlePageClick = (event, report, pageName) => {
    event.stopPropagation();

    console.log('Page clicked:', {
      pageName,
      reportId: report.id,
      selectedReportId: selectedReport?.id,
      isReportLoaded: selectedReport?.id === report.id,
      handlePageChangeAvailable: !!handlePageChange
    });

    // Check if the report is already loaded
    if (selectedReport?.id === report.id) {
      // Report is already loaded, just navigate to the page
      console.log('Report already loaded, navigating to page:', pageName);
      if (handlePageChange) {
        handlePageChange(pageName);
      } else {
        console.warn('handlePageChange is not available in context');
      }

      // Auto-close menu on mobile after selecting a page
      if (isMobile && !isReportMenuCollapsed) {
        toggleReportMenu();
      }
    } else {
      // Report is not loaded, load it first and then navigate to the page
      console.log('Report not loaded, loading report first');
      handleReportClick(report, pageName);
    }
  };

  const handleReportClick = (report, targetPageId = null) => {
    dispatch(setSelectedReport(report));

    // Store the target page ID to navigate to after report loads
    if (targetPageId) {
      sessionStorage.setItem('targetPageId', targetPageId);
    } else {
      sessionStorage.removeItem('targetPageId');
    }

    // Auto-close menu on mobile after selecting a report
    if (isMobile && !isReportMenuCollapsed) {
      toggleReportMenu();
    }

    // Scroll to top on mobile when report is clicked
    if (isMobile) {
      // Use setTimeout to ensure it happens after menu closes
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const mainContainer = document.querySelector('.main');
        if (mainContainer) {
          mainContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }

    // Fetch embed URL and token for the selected report with all required parameters
    // Convert store, district, and region IDs to arrays if they aren't already
    // Use store_ids for store_covering and district levels, otherwise use store_id
    const levelName = empInfo?.level_name?.toLowerCase();
    const storeIds = (levelName === 'store_covering' )
      ? (empInfo?.store_ids
          ? (Array.isArray(empInfo.store_ids) ? empInfo.store_ids : [empInfo.store_ids])
          : [])
      : (empInfo?.store_id
          ? (Array.isArray(empInfo.store_id) ? empInfo.store_id : [empInfo.store_id])
          : []);

    // Use market_ids for store_covering and district levels, otherwise use market_id or district_id
    const districtIds = ( levelName === 'district')
      ? (empInfo?.market_ids
          ? (Array.isArray(empInfo.market_ids) ? empInfo.market_ids : [empInfo.market_ids])
          : [])
      : (empInfo?.market_id
          ? (Array.isArray(empInfo.market_id) ? empInfo.market_id : [empInfo.market_id])
          : []);

    const regionIds = empInfo?.region_id
      ? (Array.isArray(empInfo.region_id) ? empInfo.region_id : [empInfo.region_id])
      : [];

    const embedPayload = {
      reportId: report.reportId || report.report_id || report.id,
      datasetId: report.datasetId || report.dataset_id || null,
      userEmail: empInfo?.email || empInfo?.email_work || empInfo?.emp_email || "user@example.com",
      employeeId: empInfo?.employee_id || empInfo?.emp_id || empInfo?.emp_no || null,
      jobTitle: empInfo?.job_title_name || empInfo?.job_title || null,
      jobTitleGroup: empInfo?.group_name || empInfo?.job_group || null,
      storeIds: storeIds,
      districtIds: districtIds,
      regionIds: regionIds,
      companyId: empInfo?.company_id || null,
      roles: report?.role_code ? (Array.isArray(report.role_code) ? report.role_code : report.role_code.split(',').map(r => r.trim())) : [],
      identity: report.identity
    };

    console.log("Embed Payload:", embedPayload);
    console.log("Report Data:", report);
    console.log("Employee Info:", empInfo);

    dispatch(loadPowerBIEmbedUrlRequest(embedPayload));
  };

  // Filter reports based on search query (maintains API order)
  const filteredReports = reports.filter(report =>
    report.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if a report is favorite
  const isReportFavorite = (report) => {
    const registryId = report.registry_id || report.id;
    return report.is_favorite === true ||
      favorites.some(fav => (fav.registry_id || fav.id || fav.report_id) === registryId);
  };

  // Toggle favorite for a specific report
  const toggleFavorite = (event, report) => {
    event.stopPropagation(); // Prevent report selection when clicking favorite
    event.preventDefault(); // Prevent default behavior

    const registryId = report.registry_id || report.id;

    // Check if currently favorited
    const isCurrentlyFavorite = report.is_favorite === true ||
      favorites.some(fav => (fav.registry_id || fav.id || fav.report_id) === registryId);

    if (isCurrentlyFavorite) {
      dispatch(loadPowerBIRemoveFavoriteRequest(registryId));
    } else {
      dispatch(loadPowerBIAddFavoriteRequest(registryId));
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.profile-dropdown-container')) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [profileDropdownOpen]);

  return (
    <div
      className={`reports-menu ${isReportMenuCollapsed ? 'collapsed' : ''}`}
    >
      {/* Sidebar Header - Similar to Admin Portal Menu - Hidden on Mobile */}
      <div className="reports-sidebar-header d-none d-md-flex">
        {!isReportMenuCollapsed ? (
          <>
            <Col>
              <img src={logoPath} alt="logo icon" className='company-logo' />
            </Col>
            <Col xs="auto" className="toggle-menu-icon" onClick={toggleReportMenu}>
              <i className='fa fa-angle-double-left'></i>
            </Col>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '10px' }}>
            <div className="logo-collapsed">
              <img src={iconPath} alt="logo icon" className='company-logo-small' />
            </div>
          </div>
        )}
      </div>

      {/* Expand Button for Collapsed Menu - Below Logo, Above Reports */}
      {isReportMenuCollapsed && (
        <div className="menu-expand-btn d-none d-md-flex" onClick={toggleReportMenu}>
          <i className='fa fa-angle-double-right'></i>
        </div>
      )}

      {/* Home Icon - Navigate to Landing Page */}
      <div
        className={`reports-menu-item home-menu-item ${!selectedReport ? 'active' : ''}`}
        onClick={handleHomeClick}
      >
        <i className="fa fa-home"></i>
        {!isReportMenuCollapsed && <span className="report-name">Home</span>}
        {isReportMenuCollapsed && (
          <span className="tooltip-text">Home</span>
        )}
      </div>

      {/* Search Controls */}
      {!isReportMenuCollapsed && (
        <div className="reports-menu-controls">
          <div className="search-box">
            <i className="fa fa-search"></i>
            <input
              type="text"
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="reports-menu-items">
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>Loading reports...</p>
          </div>
        )}

        {error && !loading && (
          <div className="alert alert-danger m-2" role="alert" style={{ fontSize: '0.85rem' }}>
            <i className="fa fa-exclamation-triangle me-2"></i>
            Failed to load reports
          </div>
        )}

        {!loading && !error && reports.length === 0 && (
          <div className="alert alert-info m-2" role="alert" style={{ fontSize: '0.85rem' }}>
            <i className="fa fa-info-circle me-2"></i>
            No reports available
          </div>
        )}

        {!loading && !error && filteredReports.length > 0 && (
          <>
            {filteredReports.map((report) => {
              // Use custom icon from report config, fallback to bar-chart
              // API returns just the icon name (e.g., "briefcase"), not "fa-briefcase"
              const iconName = report.report_icon || report.icon || 'bar-chart';
              const isFavorite = isReportFavorite(report);
              const hasPages = report.pages && Array.isArray(report.pages) && report.pages.length > 0;
              const isExpanded = expandedReports[report.id];

              return (
                <div key={report.id} className="report-with-pages">
                  <div
                    className={`reports-menu-item ${
                      selectedReport?.id === report.id ? "active" : ""
                    } ${isReportMenuCollapsed ? "has-tooltip" : ""} ${hasPages ? "has-pages" : ""}`}
                    onClick={() => handleReportClick(report)}
                    data-tooltip={report.name}
                  >
                    <i className={`fa fa-${iconName}`}></i>
                    {!isReportMenuCollapsed && (
                      <>
                        <span className="report-name">{report.name}</span>
                        <div className="report-menu-actions">
                          {hasPages && (
                            <button
                              className="expand-icon-btn"
                              onClick={(e) => toggleReportExpansion(e, report.id)}
                              title={isExpanded ? "Collapse pages" : "Expand pages"}
                            >
                              <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                            </button>
                          )}
                          <button
                            className={`favorite-icon-btn ${isFavorite ? 'favorite-active' : ''}`}
                            onClick={(e) => toggleFavorite(e, report)}
                            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                            disabled={favoriteToggling}
                          >
                            <i className={isFavorite ? "fa fa-star" : "fa fa-star-o"}></i>
                          </button>
                        </div>
                      </>
                    )}
                    {isReportMenuCollapsed && (
                      <>
                        <span className="tooltip-text">{report.name}</span>
                        {/* Flyout submenu for collapsed mode */}
                        {hasPages && (
                          <div className="report-submenu-flyout">
                            <div className="submenu-header">{report.name}</div>
                            <div className="submenu-pages">
                              {report.pages.map((page) => {
                                const isActivePage = currentActivePage?.name === page.page_id;
                                return (
                                  <div
                                    key={page.page_id}
                                    className={`submenu-page-item ${isActivePage ? 'active' : ''}`}
                                    onClick={(e) => handlePageClick(e, report, page.page_id)}
                                  >
                                    <i className="fa fa-file-alt"></i>
                                    <span>{page.page_name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Sub-pages - Expanded mode */}
                  {!isReportMenuCollapsed && hasPages && isExpanded && (
                    <div className="report-pages-list">
                      {report.pages.map((page) => {
                        const isActivePage = currentActivePage?.name === page.page_id;
                        return (
                          <div
                            key={page.page_id}
                            className={`report-page-item ${isActivePage ? 'active' : ''}`}
                            onClick={(e) => handlePageClick(e, report, page.page_id)}
                          >
                            <i className="fa fa-file-alt"></i>
                            <span className="page-name">{page.page_name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Profile Section at Bottom */}
      <div className="profile-section-bottom">
        <div
          className="profile-dropdown-container"
          onClick={(e) => {
            e.stopPropagation();
            setProfileDropdownOpen(!profileDropdownOpen);
          }}
        >
          <div className="profile-trigger">
            <div className="profile-avatar">
              <i className="fa fa-user"></i>
            </div>
            {!isReportMenuCollapsed && (
              <>
                <div className="profile-info">
                  <p className="profile-name">{empInfo?.first_name} {empInfo?.last_name}</p>
                  <p className="profile-role">{empInfo?.job_title || empInfo?.job_title_name}</p>
                </div>
                <i className={`fa fa-chevron-${profileDropdownOpen ? 'up' : 'down'} profile-chevron`}></i>
              </>
            )}
          </div>

          {/* Dropdown for expanded menu */}
          {!isReportMenuCollapsed && profileDropdownOpen && (
            <div className="profile-dropdown-panel" onClick={(e) => e.stopPropagation()}>
              {/* User Info Header */}
              <div className="profile-panel-header">
                <div className="profile-panel-avatar">
                  <span>{empInfo?.first_name?.charAt(0)}{empInfo?.last_name?.charAt(0)}</span>
                </div>
                <div className="profile-panel-info">
                  <p className="profile-panel-name">{empInfo?.first_name} {empInfo?.last_name}</p>
                  <p className="profile-panel-role">{empInfo?.job_title || empInfo?.job_title_name}</p>
                </div>
              </div>

              {/* Portal Selection Buttons */}
              {/* <div className="profile-portal-buttons">
                <button
                  className="portal-btn portal-btn-admin"
                  onClick={() => {
                    navigate("/dashboard");
                    setProfileDropdownOpen(false);
                  }}
                >
                  Admin Portal
                </button>
                <button
                  className="portal-btn portal-btn-report active"
                  onClick={() => {
                    // Already in Report Portal
                    setProfileDropdownOpen(false);
                  }}
                >
                  Report Portal
                </button>
              </div> */}

              {/* Profile Actions */}
              <div className="profile-actions">
                <div className="profile-action-item" onClick={() => {
                  dispatch(setProfileView(true));
                  setProfileDropdownOpen(false);
                }}>
                  <i className="fa fa-user"></i>
                  <span>Profile</span>
                </div>
                <div className="profile-action-item" onClick={() => {
                  window.location.href = "/logout";
                }}>
                  <i className="fa fa-sign-out-alt"></i>
                  <span>Logout</span>
                </div>
              </div>
            </div>
          )}

          {/* Click submenu for collapsed menu */}
          {isReportMenuCollapsed && profileDropdownOpen && (
            <div className="profile-submenu-collapsed" onClick={(e) => e.stopPropagation()}>
              {/* User Info Header */}
              <div className="profile-panel-header">
                <div className="profile-panel-avatar">
                  <span>{empInfo?.first_name?.charAt(0)}{empInfo?.last_name?.charAt(0)}</span>
                </div>
                <div className="profile-panel-info">
                  <p className="profile-panel-name">{empInfo?.first_name} {empInfo?.last_name}</p>
                  <p className="profile-panel-role">{empInfo?.job_title || empInfo?.job_title_name}</p>
                </div>
              </div>

              {/* Portal Selection Buttons */}
              {/* <div className="profile-portal-buttons">
                <button
                  className="portal-btn portal-btn-admin"
                  onClick={() => {
                    navigate("/dashboard");
                    setProfileDropdownOpen(false);
                  }}
                >
                  Admin Portal
                </button>
                <button
                  className="portal-btn portal-btn-report active"
                  onClick={() => {
                    // Already in Report Portal
                    setProfileDropdownOpen(false);
                  }}
                >
                  Report Portal
                </button>
              </div> */}

              {/* Profile Actions */}
              <div className="profile-actions">
                <div className="profile-action-item" onClick={() => {
                  dispatch(setProfileView(true));
                  setProfileDropdownOpen(false);
                }}>
                  <i className="fa fa-user"></i>
                  <span>Profile</span>
                </div>
                <div className="profile-action-item" onClick={() => {
                  window.location.href = "/logout";
                }}>
                  <i className="fa fa-sign-out-alt"></i>
                  <span>Logout</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsMenu;
